/**
 * A counter number that counts up and bumps ONLY when the server's value
 * genuinely increased (see liveCounts.ts) — never on first load or on a
 * decrease. Always settles on the exact `value` it was given. tabular-nums
 * and a minimum width keep it from shifting its neighbours (9 -> 10, 99 -> 100).
 * Reduced motion: no count-up and no scale, just a brief highlight.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../dashboard/useCountUp';
import { cx } from '../../ui/cx';
import { PULSE_MS, useCountPulse, type CountName } from './liveCounts';
import styles from './LiveCount.module.css';

export interface LiveCountProps {
  name: CountName;
  value: number;
  format?: (n: number) => string;
  className?: string | undefined;
}

const COUNT_UP_MS = 420;

export function LiveCount({ name, value, format = String, className }: LiveCountProps) {
  const pulse = useCountPulse(name);
  const initialToken = useRef(pulse?.token ?? 0);
  const [shown, setShown] = useState<number | null>(null);
  const [active, setActive] = useState(false);
  const token = pulse?.token ?? 0;
  const from = pulse?.from ?? 0;
  const to = pulse?.to ?? 0;

  useEffect(() => {
    if (token === initialToken.current) return undefined;
    setActive(true);
    let frame = 0;
    if (!prefersReducedMotion() && typeof requestAnimationFrame === 'function') {
      const start = performance.now();
      const tick = () => {
        const t = Math.min(1, (performance.now() - start) / COUNT_UP_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        setShown(t < 1 ? Math.round(from + (to - from) * eased) : null);
        if (t < 1) frame = requestAnimationFrame(tick);
      };
      setShown(from);
      frame = requestAnimationFrame(tick);
    }
    const timer = setTimeout(() => {
      cancelAnimationFrame(frame);
      setActive(false);
      setShown(null);
    }, PULSE_MS);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [token, from, to]);

  return <span className={cx(styles.count, active && styles.bump, className)}>{format(shown ?? value)}</span>;
}
